import {
    Features,
    getFeatureFlagConfig,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { useMemo } from 'react';
import useSWR from 'swr';

import {
    getConnections,
    getConnectors,
} from '../../../services/connector/ConnectorService';
import { LlmConnectorType } from '../../../services/llm-gateway/LlmGateway.types';
import {
    getAvailableLlms,
    getConfiguredLlms,
    getOperationGroupName,
    useFilteredProductConfigurations,
} from '../../../services/llm-gateway/LlmGateway.utils';
import {
    getByoProductConfigurations,
    getByoProductLlmConfigurations,
    getVendorLlmApiFlavors,
    getVendorToLlmsMappings,
} from '../../../services/llm-gateway/LlmGatewayService';
import { getFolder } from '../../../services/orchestrator/OrchFolderService';
import { getTenantById } from '../../../services/organization/TenantService';

export const useAddEditConfigurationViewModelV2 = (
    organizationId: string,
    tenantId: string,
    folderId: string,
    configurationId?: string,
    productParam?: string,
    featureParam?: string,
) => {
    const {
        data: tenant, isLoading: isTenantLoading,
    } = useSWR(tenantId ? {
        url: 'tenant',
        id: tenantId,
    } : null, getTenantById);

    const {
        data: folder, isLoading: isFolderLoading,
    } = useSWR(
        tenantId && folderId ? [ organizationId, tenantId, folderId ] : null,
        () => getFolder({
            accountLogicalName: organizationId,
            tenantName: tenantId,
        }, folderId));

    const {
        data: productLlmConfigurations, isLoading: areProductLlmConfigurationsLoading,
    } = useSWR(tenantId ? [ 'product-llm-configurations', organizationId, tenantId ] : null, () => getByoProductLlmConfigurations(organizationId, tenantId));

    const editedProductLlmConfiguration = useMemo(() =>
        productLlmConfigurations
            ?.find(config => config.id === configurationId)
    , [ productLlmConfigurations, configurationId ]);

    const {
        data: supportedLlmsPerConnectorType, isLoading: areSupportedLlmsPerConnectorTypeLoading,
    } = useSWR(tenantId ? [ 'supported-llms-per-connector-type', organizationId, tenantId ] : null, () => getVendorToLlmsMappings(organizationId, tenantId));

    const {
        data: vendorLlmApiFlavors, isLoading: areVendorLlmApiFlavorsLoading,
    } = useSWR(tenantId ? [ 'vendor-llm-api-flavors', organizationId, tenantId ] : null, () => getVendorLlmApiFlavors(organizationId, tenantId));

    const llmToConnectorTypeMapping = useMemo(() =>
        Object.entries(supportedLlmsPerConnectorType ?? {}).reduce((acc, [ connectorType, llms ]) => {
            llms.forEach(llm => {
                const llmConnectorType = connectorType as LlmConnectorType;

                if ([ LlmConnectorType.AwsBedrock, LlmConnectorType.AmazonWebServices ].includes(llmConnectorType)) {
                    acc[llm] = [ LlmConnectorType.AwsBedrock, LlmConnectorType.AmazonWebServices ];
                } else if ([ LlmConnectorType.AzureOpenAi, LlmConnectorType.OpenAi, LlmConnectorType.OpenAiV1Compatible ].includes(llmConnectorType)) {
                    acc[llm] = [ LlmConnectorType.AzureOpenAi, LlmConnectorType.OpenAi, LlmConnectorType.OpenAiV1Compatible ];
                } else {
                    acc[llm] = [ llmConnectorType ];
                }
            });
            return acc;
        }, {} as Record<string, LlmConnectorType[]>), [ supportedLlmsPerConnectorType ]);

    const {
        data: productConfigurations, isLoading: areProductConfigurationsLoading,
    } = useSWR(tenantId ? [ 'product-configurations', organizationId, tenantId ] : null, () => getByoProductConfigurations(organizationId, tenantId));

    const filteredProductConfigurations = useFilteredProductConfigurations(productConfigurations);

    const canBypassConfigurationUniqueness = useFeatureFlagValue(Features.AiTrustLayerByoLlmBypassConfigurationUniqueness.name);
    const enableByoForPassthrough = useFeatureFlagValue(Features.EnableAiTrustLayerByoForPassthrough.name);

    const availableLlms = useMemo(() => {
        if (!productLlmConfigurations || !filteredProductConfigurations) {
            return undefined;
        }

        return getAvailableLlms(productLlmConfigurations, filteredProductConfigurations, canBypassConfigurationUniqueness);
    }, [ productLlmConfigurations, filteredProductConfigurations, canBypassConfigurationUniqueness ]);

    const configuredLlms = useMemo(() => {
        if (!productLlmConfigurations) {
            return undefined;
        }

        return getConfiguredLlms(productLlmConfigurations);
    }, [ productLlmConfigurations ]);

    const availableLlmsForEditedProductLlmConfiguration = useMemo(() => {
        if (!availableLlms || !llmToConnectorTypeMapping) {
            return availableLlms;
        }

        // Determine the product/feature to ensure is available — from existing config or from explicit params
        const product = editedProductLlmConfiguration?.product ?? productParam;
        const feature = editedProductLlmConfiguration
            ? editedProductLlmConfiguration.operationGroupName
            : productParam && featureParam ? getOperationGroupName(productParam, featureParam) : undefined;

        if (!product || !feature) {
            return availableLlms;
        }

        const result = structuredClone(availableLlms);
        const availableModels = editedProductLlmConfiguration?.llmConfigurations.map(llmConfig => llmConfig.defaultModel ?? llmConfig.desiredModel);

        if (!availableLlms[product]) {
            result[product] = { [feature]: [] };
        } else if (!availableLlms[product][feature]) {
            result[product][feature] = [];
        }

        availableModels?.forEach(model => {
            if (model in llmToConnectorTypeMapping && !result[product][feature].includes(model)) {
                result[product][feature].push(model);
            }
        });

        return result;
    }, [ editedProductLlmConfiguration, availableLlms, llmToConnectorTypeMapping, productParam, featureParam ]);

    const canUseByoAiGateway = useFeatureFlagValue(Features.EnableAiTrustLayerByoAiGateway.name);

    let byoAiGatewayCustomConnectors: string[];

    try {
        byoAiGatewayCustomConnectors = JSON.parse(getFeatureFlagConfig(Features.AiTrustLayerByoAiGatewayCustomConnectors.name)) as string[];
    } catch {
        byoAiGatewayCustomConnectors = [];
    }

    const {
        data: integrationServiceConnections, isLoading: areIntegrationServiceConnectionsLoading,
    } = useSWR(
        tenantId ? [ 'integration-service-connections', organizationId, tenantId ] : null,
        () => getConnections(organizationId, tenantId));

    const {
        data: integrationServiceConnectors, isLoading: areIntegrationServiceConnectorsLoading,
    } = useSWR(
        tenantId ? {
            url: 'integration-services-connectors',
            organizationId,
            tenantId,
        } : null,
        () => getConnectors(organizationId, tenantId));

    return {
        tenant,
        folder,
        editedProductLlmConfiguration,
        filteredProductConfigurations,
        llmToConnectorTypeMapping,
        vendorLlmApiFlavors,
        configuredLlms,
        availableLlmsForEditedProductLlmConfiguration,
        canUseByoAiGateway,
        enableByoForPassthrough,
        byoAiGatewayCustomConnectors,
        integrationServiceConnections,
        integrationServiceConnectors,
        isViewLoading:
            isTenantLoading || isFolderLoading || areProductLlmConfigurationsLoading ||
            areSupportedLlmsPerConnectorTypeLoading || areVendorLlmApiFlavorsLoading || areProductConfigurationsLoading ||
            areIntegrationServiceConnectionsLoading || areIntegrationServiceConnectorsLoading,
    };
};
